import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { DriverLoadingPage, triggerToastDS } from "@qivia/ui";
import { ButtonDS } from "@qivia/ui/src/designSystem/components/Button";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectDriver } from "../../qrCode/driverKnownSlice";
import {
  createCorrectiveVehicleRestitutionAsync,
  selectCreateCorrectiveVehicleRestitutionStatus,
  selectInitialVehicleRestitution,
  selectVehicleRestitutionPictures,
  vehicleSessionsSlice,
} from "../vehicleRestitution/vehicleSessionsSlice";
import { Page } from "../../components/Page";
import { VehicleConditionForm } from "../shared/vehicleConditionForm";
import "../../../i18n/i18n";

interface RestitutionCorrectionStepProps {
  mileage: string;
  setMileage: (value: string) => void;
  hasTelemetryMileage: boolean;
  isCabinCleaned: boolean;
  setIsCabinCleaned: Dispatch<SetStateAction<boolean>>;
  isTrunkCleaned: boolean;
  setIsTrunkCleaned: Dispatch<SetStateAction<boolean>>;
  observations: string;
  setObservations: (value: string) => void;
  uploadedPictures: { uuid: string; url: string }[];
  setOldPictures: Dispatch<SetStateAction<{ uuid: string; url: string }[]>>;
  newPhotos: File[];
  setNewPhotos: (files: File[]) => void;
  onReturn: () => void;
}

export const RestitutionCorrectionStep = (
  props: RestitutionCorrectionStepProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const driver = useAppSelector(selectDriver);
  const initialRestitution = useAppSelector(selectInitialVehicleRestitution);
  const initialPictures = useAppSelector(selectVehicleRestitutionPictures);
  const createCorrectiveStatus = useAppSelector(
    selectCreateCorrectiveVehicleRestitutionStatus,
  );

  const [displayError, setDisplayError] = useState<boolean>(false);

  useEffect(() => {
    if (createCorrectiveStatus === "failed") {
      triggerToastDS(t("restitution.correction.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateCorrectiveVehicleRestitutionStatus(),
      );
    }
  }, [createCorrectiveStatus, t, dispatch]);

  const { setOldPictures } = props;
  const handleRemoveOldPicture = useCallback(
    (uuid: string) => {
      setOldPictures((prev) => prev.filter((picture) => picture.uuid !== uuid));
    },
    [setOldPictures],
  );

  const isRestitutionCorrected = useMemo(() => {
    if (!initialRestitution || !initialPictures) return false;
    const observations = props.observations === "" ? null : props.observations;
    const mileageChanged =
      !props.hasTelemetryMileage &&
      Number(props.mileage) !== initialRestitution.mileage;
    const scalarsChanged =
      mileageChanged ||
      props.isCabinCleaned !== initialRestitution.isCabinCleaned ||
      props.isTrunkCleaned !== initialRestitution.isTrunkCleaned ||
      observations !== initialRestitution.observations;
    const isThereOldPicturesRemoved =
      props.uploadedPictures.length !== initialPictures.length;
    const newPicturesUploaded = props.newPhotos.length > 0;
    return scalarsChanged || newPicturesUploaded || isThereOldPicturesRemoved;
  }, [
    initialRestitution,
    initialPictures,
    props.mileage,
    props.hasTelemetryMileage,
    props.isCabinCleaned,
    props.isTrunkCleaned,
    props.observations,
    props.newPhotos,
    props.uploadedPictures,
  ]);

  const onSubmit = useCallback(async () => {
    setDisplayError(!isRestitutionCorrected);
    if (!driver || !initialRestitution || !isRestitutionCorrected) return;
    const picturesToUpload = await Promise.all(
      props.newPhotos.map(async (file) => {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileBuffer = btoa(
          Array.from(bytes, (b) => String.fromCharCode(b)).join(""),
        );
        return { originalFileName: file.name, fileBuffer };
      }),
    );
    void dispatch(
      createCorrectiveVehicleRestitutionAsync({
        driverUuid: driver,
        mileage: Number(props.mileage),
        isCabinCleaned: props.isCabinCleaned,
        isTrunkCleaned: props.isTrunkCleaned,
        observations: props.observations === "" ? null : props.observations,
        initialRestitution: initialRestitution.uuid,
        initialPicturesToKeep: props.uploadedPictures.map(
          (picture) => picture.uuid,
        ),
        picturesToUpload,
      }),
    );
  }, [
    dispatch,
    driver,
    initialRestitution,
    isRestitutionCorrected,
    props.newPhotos,
    props.mileage,
    props.isCabinCleaned,
    props.isTrunkCleaned,
    props.observations,
    props.uploadedPictures,
  ]);

  if (createCorrectiveStatus === "processing") return <DriverLoadingPage />;

  return (
    <Page
      title={t("restitution.correction.title")}
      returnButton
      returnButtonAction={props.onReturn}
      withoutHeaderSpacer
      helpButton
      bottomButtonBackgroundColor="white"
      withShadow
      bottomButton={
        <ButtonDS
          format="fill"
          buttonType="primary"
          text={t("restitution.correction.button.validate")}
          disabled={!isRestitutionCorrected}
          onClick={() => void onSubmit()}
        />
      }
    >
      <VehicleConditionForm
        mileage={props.mileage === "" ? "" : Number(props.mileage)}
        setMileage={(value) => props.setMileage(String(value))}
        hasTelemetryMileage={props.hasTelemetryMileage}
        isCabinCleaned={props.isCabinCleaned}
        setIsCabinCleaned={props.setIsCabinCleaned}
        isTrunkCleaned={props.isTrunkCleaned}
        setIsTrunkCleaned={props.setIsTrunkCleaned}
        observations={props.observations}
        setObservations={props.setObservations}
        photos={props.newPhotos}
        setPhotos={props.setNewPhotos}
        oldPhotos={props.uploadedPictures}
        onRemoveOldPhotos={handleRemoveOldPicture}
        formError={
          displayError && !isRestitutionCorrected
            ? t("restitution.correction.noChange.error") || ""
            : undefined
        }
      />
    </Page>
  );
};
